import { FC, useContext, useEffect, useRef, useState } from 'react';

import { Camera, CameraResultType, Photo } from '@capacitor/camera';
import { Filesystem } from '@capacitor/filesystem';

import {
  IonBadge,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonImg,
  IonInput,
  IonLabel,
  IonModal,
  IonPage,
  IonSkeletonText,
  IonSpinner,
  IonText,
  isPlatform,
  useIonRouter,
  useIonToast,
} from '@ionic/react';
import cx from 'classnames';

import Avatar from 'src/components/Avatar/Avatar';

import { RouteComponentProps } from 'react-router';

import { ArrowRight, CloseIcon, PlusIcon } from 'src/assets/icons';
import './TradeChat.scss';
import { fetchChat, insertMessage, subscribeToChatMessages } from '../../api';
import AppContext from 'src/AppContext';
import { Chat, ChatMessage, TradeStatus } from 'src/types';
import { currencyFormatter, formatTradeStatus, koboToNaira } from 'src/helpers';
import { useFormik } from 'formik';
import { isApolloError } from '@apollo/client';
import { cloneDeep } from 'lodash';
import { format } from 'date-fns';

import If from 'src/components/If';
import fileUploader from 'src/helpers/fileUploader';
import { tradeDetailsPath } from 'src/data/pageRoutes';
import { Capacitor } from '@capacitor/core';
import { chatValidationSchema } from '../../validators';

interface FetchChatState {
  chat: Chat | null;
  isFetchingChat: boolean;
  chatSubscription: any;
  selectedImage: {
    url: string;
    type: string;
  };
}

interface ChatFormValues {
  body: string;
  file: Photo | null;
  chatId: string;
}

type TradeChatProps = RouteComponentProps<{
  id: string;
}>;

const TradeChat: FC<TradeChatProps> = ({ match }) => {
  const chatId = match.params.id;
  const { user } = useContext(AppContext);
  const [present] = useIonToast();
  const router = useIonRouter();
  const isHybrid = isPlatform('hybrid');

  const [state, setState] = useState<FetchChatState>({
    chat: null,
    isFetchingChat: true,
    chatSubscription: null,
    selectedImage: {
      url: '',
      type: '',
    },
  });

  const handleStateUpdate = (newState: Partial<FetchChatState>) => {
    setState((_state) => ({ ..._state, ...newState }));
  };

  const handleMessageUpdate = (newMessage: ChatMessage) => {
    setState((_state) => {
      if (_state.chat) {
        return {
          ..._state,
          chat: {
            ..._state.chat,
            messages: [..._state.chat?.messages, newMessage],
          },
        };
      }
      return _state;
    });
  };

  const scrollContentRef = useRef<HTMLIonContentElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollContentRef.current?.scrollToBottom(300);
    }, 100);
  };

  const initialValues: ChatFormValues = {
    file: null,
    body: '',
    chatId,
  };

  const uploadImage = async (photo: Photo) => {
    let file = null;
    const imageType = photo.format;
    if (isHybrid) {
      const fileRes = await Filesystem.readFile({
        path: photo.path!,
      });
      const fileBase64 = `data:image/${imageType};base64,${fileRes.data}`;
      const blob = new Blob([fileBase64], { type: imageType });
      file = new File([blob], `${user?.firstname}-${firstName}${Date.now()}`, {
        type: imageType,
      });
    } else {
      const fileRes = await fetch(photo.webPath!);
      const blob = await fileRes.blob();
      file = new File([blob], `${user?.firstname}-${firstName}${Date.now()}`, {
        type: imageType,
      });
    }
    const uploadRes = await fileUploader(file, {
      upload_preset: 'chatUploads',
      public_id: `${user?.firstname}-${firstName}${Date.now()}`,
    });
    return uploadRes.secure_url;
  };

  const onSubmit = async () => {
    const data = {
      body: values.body,
      chatId: chatId,
      file_type: values.file?.format!,
      file_url: '',
    };

    if (values.file) {
      data.file_url = await uploadImage(values.file);
    }

    try {
      await insertMessage(data);
      resetForm();
    } catch (e: any) {
      if (isApolloError(e)) {
        present({
          message: e.graphQLErrors[0].message,
          duration: 3000,
          color: 'danger',
          position: 'bottom',
        });
        return;
      }
      present({
        message: 'An error occurred',
        duration: 3000,
        color: 'danger',
        position: 'bottom',
      });
    }
  };

  const {
    values,
    isValid,
    resetForm,
    handleChange,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormik<ChatFormValues>({
    onSubmit,
    initialValues,
    validateOnMount: true,
    validationSchema: chatValidationSchema,
  });

  const firstName =
    user?.id === state.chat?.trade.ad.user.id
      ? state.chat?.trade.user.firstname
      : state.chat?.trade.ad.user.firstname;

  const takePicture = async () => {
    try {
      const image = await Camera.getPhoto({
        quality: 100,
        allowEditing: false,
        resultType: CameraResultType.Uri,
      });
      setFieldValue('file', image);
    } catch (e) {
      console.log(e);
    }
  };

  const isActiveTrade = ![
    TradeStatus.COMPLETED,
    TradeStatus.CANCELLED,
  ].includes(state.chat?.trade.status!);

  useEffect(() => {
    const getChat = async () => {
      handleStateUpdate({ isFetchingChat: true });
      try {
        const res = await fetchChat(chatId as string);
        handleStateUpdate({
          chat: res.data.chat_by_pk,
          isFetchingChat: false,
        });
        scrollToBottom();
      } catch (e) {
        handleStateUpdate({
          chat: null,
          isFetchingChat: false,
        });
      }
    };

    const subscribeToNewMessages = () => {
      const subscription = subscribeToChatMessages(
        chatId as string,
        new Date().toISOString()
      ).subscribe(
        (res) => {
          if (!res.data?.message[0]) return;
          const newMessage = cloneDeep(res.data.message[0]);
          handleMessageUpdate(newMessage);
          scrollToBottom();
        },
        (e) => {
          console.log(e);
        }
      );
      handleStateUpdate({ chatSubscription: subscription });
    };

    getChat();
    subscribeToNewMessages();
  }, [chatId]);

  useEffect(() => {
    return () => {
      state.chatSubscription?.unsubscribe();
    };
  }, [state.chatSubscription]);

  return (
    <IonPage className='tradeChat'>
      <IonHeader className='tradeChat__header' translucent={true}>
        <div className='tradeChat__headerBg'></div>
        <div className='tradeChat__headerContent'>
          <div className='tradeChat__headerTop'>
            <IonButton
              fill='clear'
              routerDirection='back'
              onClick={() =>
                router.push(`${tradeDetailsPath}/${state.chat?.trade.id}`)
              }
              className='tradeChat__headerBtn tradeChat__headerBtn--close'
            >
              <CloseIcon />
            </IonButton>
            {/* <IonButton
              className='tradeChat__headerBtn tradeChat__headerBtn--menu'
              fill='clear'
            >
              <KebabIcon />
            </IonButton> */}
          </div>
          {!state.isFetchingChat ? (
            <>
              <div className='tradeChat__headerRow'>
                <div className='tradeChat__peer'>
                  <Avatar title={firstName} className='tradeChat__peerAvatar' />
                  <IonText color={'dark'}>
                    <p className='tradeChat__peerName'>{firstName}</p>
                  </IonText>
                </div>
                <IonText>
                  <p className='tradeChat__tradeStatus'>
                    {formatTradeStatus(state.chat?.trade.status!)}
                  </p>
                </IonText>
              </div>
              <div className='tradeChat__headerRow'>
                <div className='tradeChat__tradeDetails'>
                  <span>
                    <IonText color='medium'>
                      <p>Price &nbsp;</p>
                    </IonText>
                    <IonText color='medium'>
                      <p>
                        {' '}
                        - &nbsp;
                        {currencyFormatter({
                          value: state.chat?.trade.rate as number,
                          currency: state.chat?.trade.ad.destination_currency,
                        })}
                      </p>
                    </IonText>
                  </span>
                  <span>
                    <IonText color='medium'>
                      <p>Amount &nbsp;</p>
                    </IonText>
                    <IonText color='medium'>
                      <p>
                        {' '}
                        - &nbsp;
                        {currencyFormatter({
                          value: state.chat?.trade
                            .merchant_receivable_amount as number,
                          currency: state.chat?.trade.ad.source_currency,
                        })}
                      </p>
                    </IonText>
                  </span>
                </div>
                <IonText color='dark'>
                  <p className='tradeChat__tradeAmount'>
                    {currencyFormatter({
                      value: koboToNaira(
                        state.chat?.trade.customer_receivable_amount as number
                      ),
                      currency: state.chat?.trade.ad.destination_currency,
                    })}
                  </p>
                </IonText>
              </div>
            </>
          ) : (
            <>
              <div className='tradeChat__skeleton'>
                <IonSkeletonText
                  className='tradeChat__skeletonAvatar'
                  slot='start'
                  animated={true}
                ></IonSkeletonText>
                <IonLabel>
                  <IonSkeletonText animated={true}></IonSkeletonText>
                </IonLabel>
              </div>

              <div className='tradeChat__skeleton--last'>
                <IonLabel>
                  <IonSkeletonText animated={true}></IonSkeletonText>
                </IonLabel>
              </div>
            </>
          )}
        </div>
      </IonHeader>

      {!state.isFetchingChat && (
        <IonContent
          ref={scrollContentRef}
          fullscreen={true}
          className='tradeChat__content ion-padding-horizontal ion-padding-top'
        >
          <div className='tradeChat__messages'>
            {state.chat?.messages.map((message) => (
              <div
                key={message.id}
                className={cx('tradeChat__message', {
                  'tradeChat__message--mine': user?.id === message.user?.id,
                })}
              >
                <div className='tradeChat__messageContent'>
                  <If condition={message.byAdmin}>
                    <IonBadge color={'warning'}>Admin</IonBadge>
                  </If>
                  {message.file_type && (
                    <IonImg
                      className='tradeChat__messageContentImage'
                      role='button'
                      onClick={() =>
                        handleStateUpdate({
                          selectedImage: {
                            url: message.file_url,
                            type: message.file_type,
                          },
                        })
                      }
                      src={message.file_url}
                      alt={message.file_type}
                    />
                  )}
                  <p className='tradeChat__messageText'>{message.body}</p>
                  <IonText className='tradeChat__messageTime' color='secondary'>
                    <p>{format(new Date(message.created_at), 'hh:mm a')}</p>
                  </IonText>
                </div>
                <span className='tradeChat__messagePin'></span>
              </div>
            ))}
          </div>
        </IonContent>
      )}

      <If condition={isActiveTrade}>
        <IonFooter className='tradeChat__footer'>
          <form className='tradeChat__newMessage' onSubmit={handleSubmit}>
            <IonButton
              className='tradeChat__newMessageAttachment'
              color={'tertiary'}
              fill='clear'
              onClick={takePicture}
            >
              <PlusIcon />
            </IonButton>
            <IonInput
              className='tradeChat__newMessageInput'
              placeholder='Type a message...'
              name='body'
              value={values.body}
              onIonInput={handleChange}
            />
            {values.file && (
              <div className='tradeChat__newMessageImageWrapper'>
                <IonImg
                  className='tradeChat__newMessageImage'
                  src={
                    isHybrid
                      ? Capacitor.convertFileSrc(values.file.path!)
                      : values.file.webPath!
                  }
                  alt={values.file.format}
                ></IonImg>
                <IonButton
                  onClick={() => {
                    setFieldValue('file', null);
                  }}
                  className='tradeChat__newMessageClose'
                >
                  <CloseIcon width={12} />
                </IonButton>
              </div>
            )}

            <IonButton
              className='tradeChat__newMessageSend'
              fill='solid'
              type='submit'
              color={'primary'}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? (
                <IonSpinner name='crescent'></IonSpinner>
              ) : (
                <ArrowRight />
              )}
            </IonButton>
          </form>
        </IonFooter>
      </If>
      <IonModal
        isOpen={Boolean(state.selectedImage.type)}
        onDidDismiss={() =>
          handleStateUpdate({ selectedImage: { url: '', type: '' } })
        }
        className='tradeChat__fullScreenModal'
      >
        <IonButtons slot='end' className='tradeChat__fullScreenModal--btn'>
          <IonButton
            fill='clear'
            slot='end'
            onClick={() =>
              handleStateUpdate({ selectedImage: { url: '', type: '' } })
            }
            className='tradeChat__fullScreenModal--close'
          >
            <CloseIcon />
          </IonButton>
        </IonButtons>

        <IonImg
          className='tradeChat__fullScreenModal--image'
          src={state.selectedImage.url}
          alt={state.selectedImage.type}
        />
      </IonModal>
    </IonPage>
  );
};

export default TradeChat;
